import { Filesystem, Directory } from '@capacitor/filesystem';
import { getImagePresignedUrl } from 'api/common/Data.api';
import { IUser } from 'api/interfaces/user.interface';

// FileReader를 사용한 파일 읽기 (권한 재시도 포함)
const getImageInfoWithFileReader = (fileObj: any, retryCount = 0): Promise<any | null> => {
    return new Promise((resolve, reject) => {
        var reader = new FileReader();
        console.log("imageHelper:reader", reader);

        // Android 하이브리드 앱에서 발생하는 NotReadableError 방지
        const handleFileRead = () => {
            try {
                console.log("imageHelper:reader.readAsDataURL(fileObj) - 시도:", retryCount + 1);
                reader.readAsDataURL(fileObj);
                console.log("imageHelper:reader.readAsDataURL(fileObj) - 성공");
            } catch (error) {
                console.error("imageHelper:FileReader error", error);

                // Android에서 권한 문제로 인한 첫 번째 실패 시 재시도
                if (retryCount < 2 && /Android/i.test(navigator.userAgent)) {
                    console.log("Android 권한 문제 감지, 재시도 중...");
                    setTimeout(() => {
                        getImageInfoWithFileReader(fileObj, retryCount + 1)
                            .then(resolve)
                            .catch(reject);
                    }, 500); // 0.5초 후 재시도
                    return;
                }

                reject(new Error('파일 읽기 실패'));
            }
        };

        reader.onload = (evt: any) => {
            console.log("imageHelper:evt", evt);
            var img = new Image();
            img.src = evt.target.result;

            img.onload = (e: any) => {
                console.log("imageHelper:img.onload", e);
                console.log("imageHelper:img", img);
                console.log("imageHelper:img.width", img.width);
                console.log("imageHelper:img.height", img.height);

                resolve({
                    name: fileObj.name,
                    size: fileObj.size,
                    url: evt.target.result,
                    width: img.width,
                    height: img.height,
                });
            };

            img.onerror = (err) => {
                console.error("imageHelper:img.onerror", err);
                reject(new Error('이미지 로드 실패'));
            };
        };

        reader.onerror = (error) => {
            console.error("imageHelper:reader.onerror", error);
            reject(new Error('파일 읽기 오류'));
        };

        reader.onabort = (error) => {
            console.error("imageHelper:reader.onabort", error);
            reject(new Error('파일 읽기 중단'));
        };

        // 파일 유효성 검사 추가
        if (!fileObj || !fileObj.size || fileObj.size === 0) {
            console.error("imageHelper:Invalid file object");
            reject(new Error('유효하지 않은 파일'));
            return;
        }

        // Android에서 파일 읽기 시도
        handleFileRead();
    });
};

// Capacitor를 사용한 파일 읽기
const getImageInfoWithCapacitor = async (fileObj: any): Promise<any | null> => {
    try {
        const result = await Filesystem.readFile({
            path: fileObj.webkitRelativePath || fileObj.name,
            directory: Directory.Documents
        });

        return {
            name: fileObj.name,
            size: fileObj.size,
            url: `data:${fileObj.type};base64,${result.data}`,
            width: 0,
            height: 0,
        };
    } catch (error) {
        console.error('Capacitor 파일 읽기 실패:', error);
        return null;
    }
};

export const getImageInfo = async (fileObj: any): Promise<any | null> => {
    console.log("imageHelper:getImageInfo", fileObj);

    // Android에서 갤러리 권한 문제 대응
    const isAndroid = /Android/i.test(navigator.userAgent);
    const isInAppWebView = (window as any).flutter_inappwebview;

    // Android InAppWebView에서 갤러리 접근 시 권한 대기
    if (isAndroid && isInAppWebView) {
        console.log("Android InAppWebView 환경에서 권한 대기 중...");
        await new Promise(resolve => setTimeout(resolve, 100)); // 0.1초 대기
    }

    // 1단계: FileReader로 먼저 시도
    try {
        const fileReaderResult = await getImageInfoWithFileReader(fileObj);
        if (fileReaderResult) {
            return fileReaderResult;
        }
    } catch (error) {
        console.error('FileReader 방식 실패:', error);
    }

    // 2단계: FileReader 실패 시 Capacitor 시도 (Android InAppWebView 환경에서만)

    if (isInAppWebView && isAndroid) {
        try {
            const capacitorResult = await getImageInfoWithCapacitor(fileObj);
            if (capacitorResult) {
                return capacitorResult;
            }
        } catch (error) {
            console.error('Capacitor 방식도 실패:', error);
        }
    }

    // 3단계: 모든 방식 실패 시 에러 발생
    throw new Error('파일을 읽을 수 없습니다. 파일 권한을 확인해주세요.');
};

export const getImageInfoByUrl = async (url: string): Promise<any | null> => {
    const result = new Promise((resolve, reject) => {
        var img = new Image();
        img.src = url;

        img.onload = (e: any) => {
            console.log(e);
            console.log(img);
            console.log(img.width, img.height);

            resolve({
                url: url,
                width: img.width,
                height: img.height,
            });
        };

        img.onerror = (err) => {
            console.error(err);
            resolve(null);
        };
    });

    return result;
};

export const getProfileImageUrlByAccountTypeCode = (user: IUser, width: number = 32, height: number = 32) => {
    console.log("getProfileImageUrlByAccountTypeCode", { user });
    if (user.userStudent) {
        return getProfileImageUrl(user.userStudent.profileImage, width, height);
    } else if (user.userAgency) {
        return getAgencyLogoImageUrl(user.userAgency.agency.logoImage, width, height);
    } else if (user.userKoach) {
        return getProfileImageUrl(user.userKoach.profileImage, width, height);
    } else if (user.userSchool) {
        return getProfileImageUrl(user.userSchool.logoImage, width, height);
    } else {
        return "/imgs/dummy/basic-img.png";
    }
}

export const getAgencyLogoImageUrl = (agencyLogoImageInfo: any, width: number = 720, height: number = null) => {
    if (agencyLogoImageInfo) {
        return getImageUrl(agencyLogoImageInfo, width, height);
    } else {
        return "/imgs/dummy/basic-img.png";
    }
};

export const getProfileImageUrl = (profileImageInfo: any, width: number = 720, height: number = null) => {
    // console.log("profileImageInfo", profileImageInfo);
    if (profileImageInfo) {
        return getImageUrl(profileImageInfo, width, height);
    } else {
        return "/imgs/dummy/basic-img.png";
    }
};

export const getBusinessCardImageUrl = (imageInfo: any, width: number = 720, height: number = null) => {
    if (imageInfo) {
        return getImageUrl(imageInfo, width, height);
    } else {
        return "/imgs/dummy/basic-img.png";
    }
};

export const getVideoThumbnailImageUrl = async (thumbnailImageInfo: any, width: number = 400, height: number = 300) => {
    if (thumbnailImageInfo) {

        let url = "";
        console.log("thumbnailImageInfo", { thumbnailImageInfo });
        if (!thumbnailImageInfo?.isPublic) {
            const presignedResponse = await getImagePresignedUrl(thumbnailImageInfo.id);
            if (presignedResponse?.result) {
                return presignedResponse.result;
            } else {
                return "/imgs/common/video-thumbnail-empty.png";
            }
        }

        if (thumbnailImageInfo.url) {
            url = thumbnailImageInfo.url;
        } else if (thumbnailImageInfo.subUrl) {
            url = `${process.env.REACT_APP_PUBLIC_RESOURCE_URL_PREFIX}${thumbnailImageInfo.subUrl}`;
        } else {
            return "/imgs/common/video-thumbnail-empty.png";
        }

        if (!url.toLowerCase().endsWith('.svg')) {
            if (width) {
                url += `?w=${width}`;
            }
            if (height) {
                url += `&h=${height}`;
            }
        }
        return url;
    } else {
        return "/imgs/common/video-thumbnail-empty.png";
    }
};

export const getImageUrlWithPresignedUrl = async (imageInfo: any, width: number = 720, height: number = null) => {
    // console.log("imageInfo", imageInfo);
    if (imageInfo) {
        let url = "";
        if (!imageInfo.isPublic) {
            const presignedResponse = await getImagePresignedUrl(imageInfo.id);
            if (presignedResponse?.result) {
                url = presignedResponse.result;
            } else {
                return "/imgs/dummy/basic-img.png";
            }
        } else {
            if (imageInfo.url) {
                url = imageInfo.url;
            } else if (imageInfo.subUrl) {
                url = `${process.env.REACT_APP_PUBLIC_RESOURCE_URL_PREFIX}${imageInfo.subUrl}`;
            } else {
                return "/imgs/dummy/basic-img.png";
            }
        }

        if (!url.toLowerCase().endsWith('.svg')) {
            if (width) {
                url += `?w=${width}`;
            }
            if (height) {
                url += `&h=${height}`;
            }
        }
        return url;
    } else {
        return "/imgs/dummy/basic-img.png";
    }
};

export const getImageUrl = (imageInfo: any, width: number = 720, height: number = null) => {
    // console.log("imageInfo", imageInfo);
    if (imageInfo) {
        let url = "";
        if (imageInfo.url) {
            url = imageInfo.url;
        } else if (imageInfo.subUrl) {
            url = `${process.env.REACT_APP_PUBLIC_RESOURCE_URL_PREFIX}${imageInfo.subUrl}`;
        } else {
            return "/imgs/dummy/basic-img.png";
        }

        if (!url.toLowerCase().endsWith('.svg')
            && !url.toLowerCase().endsWith('.webp')
        ) {
            if (width) {
                url += `?w=${width}`;
            }
            if (height) {
                url += `&h=${height}`;
            }
        }
        return url;
    } else {
        return "/imgs/dummy/basic-img.png";
    }
};

/**
 * 파일의 MIME 타입(이미지 타입)을 추론하는 함수입니다.
 * 
 * file.type 값이 존재하면 그것을 반환하고,
 * 없다면 파일의 앞부분(12바이트)의 바이너리 시그니처(매직넘버)를 읽어서 주요 이미지 포맷(JPEG/PNG/WEBP/HEIC 등)을 판별합니다.
 * 
 * - JPEG: 0xFF 0xD8
 * - PNG: 0x89 0x50 0x4E 0x47
 * - WEBP: 'RIFF'(0x52 0x49 0x46 0x46)
 * - HEIC/HEIF/AVIF: 5~8번째에 'ftyp'
 * 
 * 그 외에는 기본값 'application/octet-stream'을 반환합니다.
 * 
 * @param file 판별할 파일 객체
 * @returns string 해당 파일의 이미지 MIME 타입
 */
export const sniffType = async (file: File): Promise<string> => {
    if (file.type) return file.type; // image/jpeg, image/heic ...
    const head = new Uint8Array(await file.slice(0, 12).arrayBuffer());
    const isJPEG = head[0] === 0xFF && head[1] === 0xD8;
    const isPNG = head[0] === 0x89 && head[1] === 0x50 && head[2] === 0x4E && head[3] === 0x47;
    const isRIFF = head[0] === 0x52 && head[1] === 0x49 && head[2] === 0x46 && head[3] === 0x46; // WEBP
    const isISOBMFF = (head[4] === 0x66 && head[5] === 0x74 && head[6] === 0x79 && head[7] === 0x70); // HEIC/HEIF/AVIF
    if (isJPEG) return 'image/jpeg';
    if (isPNG) return 'image/png';
    if (isRIFF) return 'image/webp';
    if (isISOBMFF) return 'image/heic'; // 실제는 heic/heif/avif 중 하나
    return 'application/octet-stream';
}

// lib.dom.d.ts 기준 imageOrientation 타입에는 'from-image'가 허용되지 않으므로, 타입 단언을 사용하여 오류를 방지합니다.
export const viaCreateImageBitmap = async (file: File) => {
    try {
        // 타입스크립트 타입 오류 방지: 'from-image'는 실제로 지원되지만 타입에는 없음
        const bmp = await createImageBitmap(file, { imageOrientation: 'from-image' as any });
        const { width, height } = bmp; // 회전 반영된 크기
        bmp.close?.();
        return { width, height, method: 'createImageBitmap' };
    } catch (e) {
        return { width: 0, height: 0, method: 'createImageBitmap', error: e };
    }
}

export const viaImageDecoder = async (file: File) => {
    if (!('ImageDecoder' in window)) return { width: 0, height: 0, method: 'ImageDecoder' };
    try {
        const type = await sniffType(file);
        const dec = new (window as any).ImageDecoder({ data: file.stream(), type });
        const { image } = await dec.decode({ frameIndex: 0 });
        // displayWidth/Height는 회전 적용된 표시 크기, codedWidth/Height는 원본 매트릭스
        const width = image.displayWidth ?? image.codedWidth;
        const height = image.displayHeight ?? image.codedHeight;
        image.close?.(); dec.close?.();
        return { width, height, method: 'ImageDecoder' };
    } catch (e) {
        return { width: 0, height: 0, method: 'ImageDecoder', error: e };
    }
}

export const viaImageDecode = async (file: File) => {
    try {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.src = url;
        await img.decode(); // onload 대신 decode: 안정적
        const width = img.naturalWidth, height = img.naturalHeight;
        URL.revokeObjectURL(url);
        return { width, height, method: 'img.decode' };
    } catch (e) {
        return { width: 0, height: 0, method: 'img.decode', error: e };
    }
}

export const viaCanvas = async (file: File) => {
    try {
        const bmp = await createImageBitmap(file);
        const width = bmp.width, height = bmp.height;
        bmp.close?.();
        return { width, height, method: 'canvas-bitmap' };
    } catch (e) {
        // 마지막 수단: Image + onload (최후의 최후)
        return { width: 0, height: 0, method: 'canvas-bitmap', error: e };
    }
}